import { useRouter } from 'next/router';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { SingleToolFooter, WithBreadcrumb, FontCategory } from '@quicktools/shared-components';
import { GoogleAd, useShowAd } from '@quicktools/shared-components/components/GoogleAd';
import { Editor } from '@quicktools/shared-components/components/domainSpecific/text/Editor';
import NextHead from '@quicktools/shared-components/components/common/NextHead';
import LayoutForTextApps from '@quicktools/shared-components/components/common/LayoutForTextApps/LayoutForTextApps';
import { IBreadcrumb } from '@quicktools/shared-components/components/core/Breadcrumb/types';
import getBreadcrumbSublinks from '@quicktools/shared-components/utils/breadcrumbSubLinks';
import useEditorData from '@quicktools/shared-components/hooks/useEditorData';
import useSnackbarStore from '@quicktools/shared-components/hooks/snackbar';
import SnackbarList from '@quicktools/shared-components/components/core/SnackbarList';
import { clipboardWrite } from '@quicktools/shared-components/utils/text/clipboard';
import { isIOS, isAndroid } from 'react-device-detect';
import {
  GOOGLE_AD_MANAGER_QT_RIGHT_RAIL_WEB,
  GOOGLE_AD_MANAGER_QT_RIGHT_RAIL_MOBILE,
} from '@quicktools/shared-components/services/constants';

import { CATEGORIES } from '../../constants';
import Generator from '../../components/Generator';
import getGeneratorHeading from '../../utils/common/getGeneratorHeading';
import Categories from '../../components/Categories';
import MobileCategories from '../../components/MobileCategories';
import { useStore } from '../../store';
import Header from '../../sections/Header';
import Main from '../../sections/Main';
import analytics from '../../services/analytics';
import Landing from '../Landing';

import useFontsAndCategories from './useFontsAndCategories';
import useStyles from './styles';
import IAppProps from './types';

const WEB_EDITOR_LINK =
  'https://picsart.com/create/editor?image=https%3A%2F%2Fcdn140.picsart.com%2F77017721556598651392.jpg&customSize=800x800&unit=px&category=text&text=';

function openUpstream(text: string) {
  const encoded = encodeURIComponent(text);

  switch (true) {
    case isIOS: {
      return window.open('https://picsart.onelink.me/XqUB/qtiOS');
    }
    case isAndroid: {
      return window.open('https://picsart.onelink.me/XqUB/qtAndroid');
    }
    default: {
      return window.open(`${WEB_EDITOR_LINK}${encoded}`, '_blank');
    }
  }
}

function App({
  canonicalPath,
  heading,
  headTitle,
  headDescription,
  description,
  shortDescription,
  selectedCategory,
  children,
  landingText,
  noIndex,
  hasRawTitle,
  inputPlaceholder,
}: IAppProps) {
  const classes = useStyles();
  const router = useRouter();
  const { t } = useTranslation();
  const editorExpanded = useStore((state) => state.editorExpanded);
  const setEditorExpanded = useStore((state) => state.setEditorExpanded);
  const editorData = useStore((state) => state.editorData);
  const setEditorData = useStore((state) => state.setEditorData);
  const snackItems = useSnackbarStore((state) => state.snackItems);
  const closeSnack = useSnackbarStore((state) => state.snackbarClose);
  const snackbarShowWarning = useSnackbarStore((state) => state.snackbarShowWarning);
  const showGenerateTextBanner = useStore((state) => state.showGenerateTextBanner);

  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumb[]>([]);
  const [stickyOffest, setStickyOffset] = useState(0);
  const [categories, selected, fonts, setRecentsCategoryVisible] =
    useFontsAndCategories(selectedCategory);
  const { showDesktopAd, showMobileAd } = useShowAd();

  const onCopyError = useCallback(() => snackbarShowWarning(t('error.clipboardCopy')), []);
  const onLinkButtonClick = useCallback((text) => {
    analytics.sendGaEvent('page_click', {
      action_type: 'add_to_design',
      page_path: CATEGORIES[selectedCategory]?.href,
    });
    try {
      clipboardWrite(text[0]);
    } catch (err) {
      snackbarShowWarning(t('error.clipboardCopy'));
    }
    openUpstream(text[0]);
  }, []);

  useEffect(() => {
    setBreadcrumbItems(getBreadcrumbSublinks(canonicalPath));
  }, [canonicalPath]);

  useEditorData(setEditorData, setEditorExpanded);

  useEffect(() => {
    if (selectedCategory === FontCategory.Recents && fonts.length <= 0) {
      router.push('/');
    }
  });

  return (
    <>
      <NextHead
        canonicalPath={canonicalPath}
        title={headTitle}
        description={headDescription}
        noIndex={noIndex}
        hasRawTitle={hasRawTitle}
      />
      <SnackbarList
        className={classes.notifications}
        snackItems={snackItems}
        onClose={closeSnack}
      />

      <Header />
      <WithBreadcrumb items={breadcrumbItems} />

      <LayoutForTextApps
        className={showGenerateTextBanner && classes.layout}
        stickyOffset={stickyOffest}
        sidebarChild={<Categories categories={categories} selected={selected} />}
        preHeadlineChild={<MobileCategories categories={categories} selected={selected} />}
        adChild={
          showDesktopAd ? (
            <GoogleAd id={GOOGLE_AD_MANAGER_QT_RIGHT_RAIL_WEB} className={classes.ad} />
          ) : null
        }
        listingChild={
          <Generator
            fonts={fonts}
            displayCategoryName={selectedCategory === FontCategory.All}
            setRecentsCategoryVisible={setRecentsCategoryVisible}
          />
        }
        stickyInputChild={
          <Main
            heading={heading}
            description={isMobile ? shortDescription : description}
            hasMobileDescription={true}
            onStickyOffestChange={setStickyOffset}
            inputPlaceholder={inputPlaceholder}
          />
        }
        listingBarChild={
          <div className={classes.listHeading}>{getGeneratorHeading(selectedCategory)}</div>
        }
      >
        {showMobileAd && (
          <GoogleAd id={GOOGLE_AD_MANAGER_QT_RIGHT_RAIL_MOBILE} className={classes.mobileAd} />
        )}
        {landingText && <Landing text={landingText} />}
        {children}
      </LayoutForTextApps>

      <div className={classes.footerContainer}>
        <SingleToolFooter className={classes.footer} analytics={analytics} />
      </div>

      <Editor
        heading="Editor"
        data={editorData}
        analytics={analytics}
        setData={setEditorData}
        onCloseClick={() => null}
        expanded={editorExpanded}
        onCopyError={onCopyError}
        className={classes.editor}
        onExpand={setEditorExpanded}
        onLinkButtonClick={onLinkButtonClick}
        defaultText="Copy a font, emoji, or symbol above or write/paste here to get started..."
        onCopyClick={() => {
          analytics.fontArrayCopy(editorData[0]);
          analytics.sendBrazeEvent('font_array_copy');
        }}
      />
    </>
  );
}

export default App;
